import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import { Link } from "react-router-dom";
import TutorLayout from "./TutorLayout";

function TutorClassworkPage() {
  const [students, setStudents] = useState({});
  const [selectedStudentUID, setSelectedStudentUID] = useState("");
  const [lessons, setLessons] = useState([]);
  const [openFolders, setOpenFolders] = useState({});
  const [editingFolder, setEditingFolder] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editResources, setEditResources] = useState([]);
  const [editAssignments, setEditAssignments] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  

  useEffect(() => {
    const fetchStudents = async () => {
      const userSnap = await getDocs(
        query(collection(db, "users"), where("role", "==", "student"))
      );
      const users = userSnap.docs.reduce((acc, doc) => {
        const data = doc.data();
        acc[data.uid] = data;
        return acc;
      }, {});
      setStudents(users);
    };

    fetchStudents();
  }, []);

  useEffect(() => {
    if (!selectedStudentUID) return;

    const fetchLessons = async () => {
      const [assignSnap, folderSnap] = await Promise.all([
        getDocs(collection(db, "assignments")),
        getDocs(collection(db, "lessonFolders")),
      ]);

      const assignments = assignSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const folders = folderSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((folder) => folder.studentUID === selectedStudentUID);

      const folderLessons = folders.map((folder) => {
        const linkedAssignments = assignments.filter((a) =>
          folder.assignments.includes(a.id)
        );
        return {
          folder,
          assignments: linkedAssignments,
        };
      });

      folderLessons.sort(
        (a, b) => new Date(b.folder.createdAt) - new Date(a.folder.createdAt)
      );
      setLessons(folderLessons);
      setAllAssignments(assignments);
    };

    fetchLessons();
  }, [selectedStudentUID]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "—";

  const toggleFolder = (folderId) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const getAssignmentLink = (a) => {
    if (a.activityType === "millionaire") {
      return `/millionaire_tutor_viewer/${a.id}?studentUID=${selectedStudentUID}`;
    } else if (a.activityType === "blockbusters") {
      return `/blockbusters-welcome/${a.id}?studentUID=${selectedStudentUID}`;
    } else {
      return `/tutor-assignment-viewer/${a.id}/${selectedStudentUID}`;
    }
  };
  

  const handleDeleteFolder = async (folderId) => {
    const confirm = window.confirm("Are you sure you want to delete this folder?");
    if (!confirm) return;

    try {
      await deleteDoc(doc(db, "lessonFolders", folderId));
      setLessons((prev) => prev.filter((l) => l.folder.id !== folderId));
      alert("🗑️ Folder deleted successfully.");
    } catch (err) {
      console.error("Error deleting folder:", err);
      alert("Failed to delete folder.");
    }
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setEditTitle(folder.title);
    setEditNotes(folder.notes || "");
    setEditResources(folder.googleResources || []);
    setEditAssignments(folder.assignments || []);
  };  

  const saveFolderEdits = async () => {
    if (!editingFolder) return;
  
    try {
      const folderRef = doc(db, "lessonFolders", editingFolder.id);
      await updateDoc(folderRef, {
        title: editTitle,
        notes: editNotes,
        googleResources: editResources.filter(r => r.name && r.url),
        assignments: editAssignments,
      });
  
      const updatedAssignments = allAssignments.filter((a) =>
        editAssignments.includes(a.id)
      );
      
      setLessons((prev) =>
        prev.map((l) =>
          l.folder.id === editingFolder.id
            ? {
                ...l,
                folder: {
                  ...l.folder,
                  title: editTitle,
                  notes: editNotes,
                  googleResources: editResources,
                  assignments: editAssignments,
                },
                assignments: updatedAssignments,
              }
            : l
        )
      );
      
  
      setEditingFolder(null);
      alert("✅ Folder updated.");
    } catch (err) {
      console.error("Error updating folder:", err);
      alert("Failed to update folder.");
    }
  };
  
  return (
    <TutorLayout>
      <div style={styles.container}>
        <h1 style={styles.heading}>📚 View Student Classwork</h1>

        <label style={styles.label}>Select Student:</label>
        <select
          style={styles.dropdown}
          value={selectedStudentUID}
          onChange={(e) => setSelectedStudentUID(e.target.value)}
        >
          <option value="">-- Choose a student --</option>
          {Object.entries(students).map(([uid, user]) => (
            <option key={uid} value={uid}>
              {user.displayName || user.email}
            </option>
          ))}
        </select>

        {selectedStudentUID === "" ? (
          <p style={{ marginTop: "1rem" }}>
            👆 Please select a student to view their classwork.
          </p>
        ) : lessons.length === 0 ? (
          <p style={{ marginTop: "1rem" }}>
            No folders found for this student.
          </p>
        ) : (
          <ul style={styles.list}>
            {lessons.map(({ folder, assignments }, i) => {
              const isCurrent = i === 0;
              return (
                <li
                  key={folder.id}
                  style={{
                    ...styles.card,
                    width: "260px",
                    fontSize: "0.9rem",
                    ...(isCurrent ? styles.currentLesson : {}),
                  }}
                >
                  <div
                    onClick={() => toggleFolder(folder.id)}
                    style={{
                      cursor: "pointer",
                      marginBottom: openFolders[folder.id] ? "1rem" : "0",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h3 style={{ ...styles.title, fontSize: "1rem", margin: 0 }}>
                        📖 {folder.title}
                      </h3>
                      <div style={{ display: "flex", gap: "0.3rem" }}>
  <button
    onClick={(e) => {
      e.stopPropagation();
      handleEditFolder(folder);
    }}
    style={{
      background: "#ffeaa7",
      border: "none",
      padding: "0.2rem 0.4rem",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.75rem",
      fontWeight: "600",
      transform: "translate(25px, -20px)"
    }}
  >
    ✏️ Edit
  </button>
  <button
    onClick={(e) => {
      e.stopPropagation();
      handleDeleteFolder(folder.id);
    }}
    style={{
      background: "#fab1a0",
      border: "none",
      padding: "0.2rem 0.4rem",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.75rem",
      transform: "translate(25px, -20px)"
    }}
  >
    🗑️
  </button>
</div>

                    </div>
                    <p style={{ ...styles.date, fontSize: "0.8rem" }}>
                      🗓️ {formatDate(folder.createdAt)}
                    </p>
                  </div>

                  {openFolders[folder.id] && (
                    <div style={styles.cardContent}>
                      {folder.notes && (
                        <p style={styles.assignment}>{folder.notes}</p>
                      )}

                      {folder.googleResources?.length > 0 && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <strong>📌 Resources:</strong>
                          <ul>
                            {folder.googleResources.map((res, idx) => (
                              <li key={idx}>
                                <a
                                  href={res.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {res.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {assignments.length > 0 && (
                        <div style={{ marginTop: "0.5rem" }}>
                          {assignments.map((a) => (
                            <div
                              key={a.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#f8f9ff",
                                borderRadius: "20px",
                                padding: "1rem",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                                borderLeft: "6px solid #5cb85c",
                                marginTop: "0rem",
                              }}
                            >
                              <Link
                                to={getAssignmentLink(a)}
                                style={{
                                  textDecoration: "none",
                                  color: "#0984e3",
                                  fontWeight: "500",
                                  fontSize: "1rem",
                                  width: "100%",
                                }}
                              >
                                {a.title}
                              </Link>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

{editingFolder && (
  <div style={{
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center",
    zIndex: 1000
  }}>
    <div style={{ background: "white", padding: "2rem", borderRadius: "12px", minWidth: "300px", maxWidth: "600px" }}>
      <h3>Edit Folder</h3>

      <label>Title:</label>
      <input
        value={editTitle}
        onChange={(e) => setEditTitle(e.target.value)}
        style={{ width: "100%", marginBottom: "1rem", padding: "0.5rem" }}
      />

      <label>Notes:</label>
      <textarea
        value={editNotes}
        onChange={(e) => setEditNotes(e.target.value)}
        style={{ width: "100%", marginBottom: "1rem", padding: "0.5rem" }}
      />

      <label>📎 Resources:</label>
      {editResources.map((res, index) => (
        <div key={index} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <input
            type="text"
            placeholder="Name"
            value={res.name}
            onChange={(e) => {
              const updated = [...editResources];
              updated[index].name = e.target.value;
              setEditResources(updated);
            }}
            style={{ flex: 1 }}
          />
          <input
            type="text"
            placeholder="URL"
            value={res.url}
            onChange={(e) => {
              const updated = [...editResources];
              updated[index].url = e.target.value;
              setEditResources(updated);
            }}
            style={{ flex: 2 }}
          />
          <button onClick={() => setEditResources(editResources.filter((_, i) => i !== index))}>❌</button>
        </div>
      ))}
      <button onClick={() => setEditResources([...editResources, { name: "", url: "" }])}>➕ Add Resource</button>

      <label style={{ marginTop: "1rem", display: "block" }}>📋 Assignments:</label>
      <div style={{ maxHeight: "150px", overflowY: "auto", marginBottom: "1rem" }}>
        {allAssignments.map((a) => (
          <label key={a.id} style={{ display: "block" }}>
            <input
              type="checkbox"
              checked={editAssignments.includes(a.id)}
              onChange={() => {
                setEditAssignments((prev) =>
                  prev.includes(a.id)
                    ? prev.filter((id) => id !== a.id)
                    : [...prev, a.id]
                );
              }}
            />{" "}
            {a.title || "(Untitled)"}
          </label>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem" }}>
        <button onClick={saveFolderEdits} style={{ padding: "0.5rem 1rem", backgroundColor: "#74b9ff", border: "none", borderRadius: "6px", color: "white" }}>
          💾 Save
        </button>
        <button onClick={() => setEditingFolder(null)} style={{ padding: "0.5rem 1rem", backgroundColor: "#dfe6e9", border: "none", borderRadius: "6px" }}>
          ❌ Cancel
        </button>
      </div>
    </div>
  </div>
)}

      </div>
    </TutorLayout>
  );
}

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Fredoka', cursive",
    backgroundColor: "#fefae0",
    minHeight: "100vh",
  },
  heading: {
    fontSize: "2rem",
    marginBottom: "1.5rem",
    color: "#2d3436",
  },
  label: {
    fontSize: "1rem",
    fontWeight: "bold",
    marginBottom: "0.5rem",
    display: "block",
  },
  dropdown: {
    padding: "0.6rem",
    fontSize: "1rem",
    borderRadius: "8px",
    marginBottom: "1.5rem",
    border: "1px solid #ccc",
    width: "100%",
    maxWidth: "400px",
  },
  list: {
    listStyleType: "none",
    padding: 0,
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  card: {
    background: "linear-gradient(to bottom right, #ffffff, #f0f4ff)",
    borderRadius: "12px",
    padding: "1rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
  },
  currentLesson: {
    border: "3px solid gold",
    animation: "flash 1.5s infinite alternate",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  title: {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#2d3436",
    marginBottom: "0.5rem",
  },
  date: {
    fontSize: "0.9rem",
    marginBottom: "0.8rem",
    color: "#636e72",
  },
  assignment: {
    backgroundColor: "#dfe6e9",
    padding: "0.6rem",
    borderRadius: "8px",
    fontSize: "0.85rem",
    lineHeight: "1.4",
    marginBottom: "1rem",
  },
};

export default TutorClassworkPage;
